import { useCallback, useRef } from 'react';

const serialize = (value) => {
  try {
    return JSON.stringify(value ?? null);
  } catch (error) {
    return '';
  }
};

const setIn = (obj = {}, path, value) => {
  const [key, ...rest] = path.split('.');
  return {
    ...obj,
    [key]: rest.length
      ? setIn(obj[key] || {}, rest.join('.'), value)
      : value,
  };
};

const useNodeEditor = ({
  selectedNode,
  handleSelectedNode,
  nodes,
  edges,
  editFlow,
  updateBot,
  reset,
  getValues,
}) => {
  const latest = useRef(null);
  const baseline = useRef(null);

  latest.current = {
    selectedNode,
    nodes,
    edges,
    editFlow,
    updateBot,
    getValues,
  };

  const read = useCallback(() => {
    const source = latest.current;
    const reader = source?.getValues;
    return serialize({
      data: source?.selectedNode?.data,
      values: typeof reader === 'function' ? reader() : null,
    });
  }, []);

  const markClean = useCallback(() => {
    baseline.current = read();
  }, [read]);

  const getIsDirty = useCallback(
    () => baseline.current !== null && read() !== baseline.current,
    [read]
  );

  const handleInputChange = useCallback(
    (path, value) => {
      handleSelectedNode((prev) => ({
        ...prev,
        data: setIn(prev.data, path, value),
      }));
    },
    [handleSelectedNode]
  );

  const loadData = useCallback(
    (objectData, options = { keepDefaultValues: true }) => {
      reset(objectData, options);
      markClean();
    },
    [reset, markClean]
  );

  const saveNode = useCallback(
    (data, onDone) => {
      const source = latest.current;
      const node = source?.selectedNode;
      if (!node) return Promise.resolve();

      const nextData = { ...node.data, ...data, saved: true };
      const nextNodes = (source.nodes || []).map((item) =>
        item.id === node.id ? { ...item, data: nextData } : item
      );

      handleSelectedNode((prev) => ({
        ...prev,
        data: { ...prev.data, ...data, saved: true },
      }));

      return source
        .updateBot(source.editFlow, source.edges, nextNodes)
        .then(() => {
          markClean();
          onDone?.();
        });
    },
    [handleSelectedNode, markClean]
  );

  return { handleInputChange, loadData, saveNode, getIsDirty, markClean };
};

export default useNodeEditor;
